import * as React from "react";
import { Check, X, Plus, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type MultiSelectOption = {
  id: number | string;
  name: string;
  chip_color?: string | null;
};

function sameId(a: number | string, b: number | string) {
  return String(a) === String(b);
}

export function CreatableMultiSelect({
  label,
  placeholder,
  options,
  selectedIds,
  onChange,
  onCreate,
  isCreating,
  disabled,
}: {
  label: string;
  placeholder?: string;
  options: MultiSelectOption[];
  selectedIds: (number | string)[];
  onChange: (ids: (number | string)[]) => void;
  onCreate: (name: string) => Promise<MultiSelectOption>;
  isCreating?: boolean;
  disabled?: boolean;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery("");
      setError(null);
    }
  }, [open]);

  const selected = React.useMemo(
    () => options.filter((o) => selectedIds.some((s) => sameId(s, o.id))),
    [options, selectedIds],
  );

  const trimmed = query.trim();
  const filtered = React.useMemo(() => {
    if (!trimmed) return options;
    const q = trimmed.toLowerCase();
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, trimmed]);

  const exactMatch = options.some(
    (o) => o.name.toLowerCase() === trimmed.toLowerCase(),
  );
  const showCreate = trimmed.length > 0 && !exactMatch;

  function toggle(id: number | string) {
    const present = selectedIds.some((x) => sameId(x, id));
    if (present) onChange(selectedIds.filter((x) => !sameId(x, id)));
    else onChange([...selectedIds, id]);
  }

  function remove(id: number | string) {
    onChange(selectedIds.filter((x) => !sameId(x, id)));
  }

  async function create() {
    if (!trimmed) return;
    setError(null);
    try {
      const newOpt = await onCreate(trimmed);
      onChange([...selectedIds, newOpt.id]);
      setQuery("");
      inputRef.current?.focus();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={cn(
          "flex min-h-9 w-full items-center justify-between gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm",
          "hover:bg-accent/30 disabled:opacity-50 disabled:cursor-not-allowed",
        )}
      >
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {selected.length === 0 && (
            <span className="text-muted-foreground">
              {placeholder ?? `Add ${label.toLowerCase()}…`}
            </span>
          )}
          {selected.map((opt) => (
            <span
              key={String(opt.id)}
              className={cn(
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs",
                opt.chip_color ?? "bg-slate-100 text-slate-700",
              )}
            >
              {opt.name}
              <span
                role="button"
                aria-label={`Remove ${opt.name}`}
                className="opacity-60 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  remove(opt.id);
                }}
              >
                <X className="w-3 h-3" />
              </span>
            </span>
          ))}
        </div>
        <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
          <div className="border-b border-border px-2 py-2">
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  setOpen(false);
                } else if (e.key === "Enter" && showCreate) {
                  e.preventDefault();
                  create();
                }
              }}
              placeholder={`Search ${label.toLowerCase()}…`}
              className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none px-1"
            />
          </div>

          <div className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && !showCreate && (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                No matches.
              </div>
            )}

            {filtered.map((opt) => {
              const isSel = selectedIds.some((s) => sameId(s, opt.id));
              return (
                <button
                  key={String(opt.id)}
                  type="button"
                  onClick={() => toggle(opt.id)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-accent/50 transition-colors"
                >
                  <Check
                    className={cn(
                      "w-4 h-4 flex-shrink-0",
                      isSel ? "opacity-100 text-primary" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{opt.name}</span>
                </button>
              );
            })}

            {showCreate && (
              <button
                type="button"
                onClick={() => create()}
                disabled={isCreating}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-primary hover:bg-accent/50 transition-colors disabled:opacity-50 border-t border-border/40 mt-1 pt-2"
              >
                {isCreating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                <span className="truncate">
                  Create &ldquo;{trimmed}&rdquo;
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>
      )}
    </div>
  );
}
